/**
 * BathymetryImportPage.tsx — Bathymetric survey ingestion
 *
 * Accepts CSV with rows of (x, y, depth) or (easting, northing, depth) points.
 * Computes a depth raster, statistics, contour bands and identifies the
 * dominant low spot, mean and standard deviation. Persists the parsed
 * point cloud so other pages can recompute layout against real bed.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Upload, ArrowLeft, FileText, Trash2, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, savePersisted } from '@/lib/sessionPersistence';
import { toast } from 'sonner';

const KEY = 'rb_bathymetry_v1';

interface BathyPoint {
  x: number;
  y: number;
  depth: number;
}

interface BathyState {
  filename: string;
  uploadedAt: string;
  points: BathyPoint[];
}

const DEFAULT_STATE: BathyState = {
  filename: '',
  uploadedAt: '',
  points: [],
};

function parseCSV(text: string): BathyPoint[] {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (!lines.length) return [];
  // Try to detect header
  const first = lines[0].split(/[,\t;]/);
  const hasHeader = isNaN(Number(first[0])) || isNaN(Number(first[1])) || isNaN(Number(first[2]));
  const rows = hasHeader ? lines.slice(1) : lines;
  return rows.map(line => {
    const parts = line.split(/[,\t;]/).map(s => Number(s.trim()));
    return { x: parts[0], y: parts[1], depth: Math.abs(parts[2]) };
  }).filter(p => isFinite(p.x) && isFinite(p.y) && isFinite(p.depth));
}

function fmt(n: number, d = 2) { if (!isFinite(n)) return '—'; return n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }); }

export default function BathymetryImportPage() {
  const navigate = useNavigate();
  const [state, setState] = useState<BathyState>(() => loadPersisted<BathyState>(KEY, DEFAULT_STATE));
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { savePersisted(KEY, state); }, [state]);

  const stats = useMemo(() => {
    const pts = state.points;
    if (!pts.length) return null;
    const depths = pts.map(p => p.depth);
    const min = Math.min(...depths);
    const max = Math.max(...depths);
    const mean = depths.reduce((a, b) => a + b, 0) / depths.length;
    const variance = depths.reduce((a, b) => a + (b - mean) ** 2, 0) / depths.length;
    const sd = Math.sqrt(variance);
    const xs = pts.map(p => p.x);
    const ys = pts.map(p => p.y);
    const xMin = Math.min(...xs), xMax = Math.max(...xs);
    const yMin = Math.min(...ys), yMax = Math.max(...ys);
    // Slope band: max-min / mean horizontal extent
    const extent = Math.max(xMax - xMin, yMax - yMin);
    const slopePct = extent > 0 ? ((max - min) / extent) * 100 : 0;
    // Low spot
    const lowSpot = pts.reduce((a, b) => b.depth > a.depth ? b : a, pts[0]);
    return { min, max, mean, sd, count: pts.length, xMin, xMax, yMin, yMax, slopePct, lowSpot };
  }, [state.points]);

  const handleFile = async (file: File) => {
    const text = await file.text();
    const points = parseCSV(text);
    if (!points.length) { toast.error('No valid points found in file'); return; }
    setState({ filename: file.name, uploadedAt: new Date().toISOString(), points });
    toast.success(`Imported ${points.length} bathymetric points`);
  };

  const clear = () => {
    setState(DEFAULT_STATE);
    if (fileRef.current) fileRef.current.value = '';
    toast.success('Bathymetric data cleared');
  };

  // Render the points as a depth-coloured scatter inside their bbox
  const svgW = 720, svgH = 320;
  const padding = 30;
  const scaleX = stats ? (svgW - 2 * padding) / Math.max(1, stats.xMax - stats.xMin) : 1;
  const scaleY = stats ? (svgH - 2 * padding) / Math.max(1, stats.yMax - stats.yMin) : 1;
  const sx = stats ? (x: number) => padding + (x - stats.xMin) * scaleX : (x: number) => x;
  const sy = stats ? (y: number) => svgH - padding - (y - stats.yMin) * scaleY : (y: number) => y;
  const depthColour = (d: number) => {
    if (!stats) return '#64748b';
    const frac = (d - stats.min) / Math.max(0.001, stats.max - stats.min);
    // Blue (shallow) → Dark blue (deep)
    const r = Math.round(56 + (10 - 56) * frac);
    const g = Math.round(189 + (40 - 189) * frac);
    const b = Math.round(248 + (140 - 248) * frac);
    return `rgb(${r},${g},${b})`;
  };

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={Upload}
        title="Bathymetric Survey — Data Import"
        subtitle="Upload CSV/TSV of survey points (x, y, depth). The parsed bed feeds the layout optimiser and the contour-aware module placement."
        actions={
          <button onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        }
      />

      {/* Upload */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Upload survey CSV</div>
        <div className="text-xs text-slate-400 mb-3">
          Three columns required: x (or easting), y (or northing), depth (m). Header row optional.
          Delimiters: comma, tab or semicolon. Depths are taken as absolute values.
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input ref={fileRef} type="file" accept=".csv,.tsv,.txt"
            onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
            className="text-xs text-slate-300" />
          {state.filename && (
            <>
              <span className="text-xs text-slate-400 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" /> {state.filename}
                <span className="text-slate-500"> · {new Date(state.uploadedAt).toLocaleString('en-GB')}</span>
              </span>
              <button onClick={clear}
                className="inline-flex items-center gap-1.5 text-xs text-red-300 hover:text-red-200">
                <Trash2 className="w-3.5 h-3.5" /> Clear
              </button>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      {stats ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wider">Points</div>
              <div className="text-2xl font-bold text-slate-100 mt-1">{stats.count.toLocaleString()}</div>
            </div>
            <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wider">Mean depth</div>
              <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(stats.mean, 2)} m</div>
              <div className="text-xs text-slate-500 mt-1">σ {fmt(stats.sd, 2)} m</div>
            </div>
            <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wider">Depth range</div>
              <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(stats.min, 2)}–{fmt(stats.max, 2)} m</div>
            </div>
            <div className="rounded-xl border border-amber-700 bg-amber-900/20 p-4">
              <div className="text-xs text-amber-300 uppercase tracking-wider">Bed slope (gross)</div>
              <div className="text-2xl font-bold text-amber-200 mt-1">{fmt(stats.slopePct, 2)} %</div>
            </div>
            <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wider">Low spot</div>
              <div className="text-sm font-bold text-slate-100 mt-1">{fmt(stats.lowSpot.depth, 2)} m</div>
              <div className="text-xs text-slate-500 mt-1">@ ({fmt(stats.lowSpot.x, 1)}, {fmt(stats.lowSpot.y, 1)})</div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
            <div className="text-sm font-semibold text-slate-100 mb-2">Depth-coloured point cloud</div>
            <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full h-auto bg-slate-950 rounded">
              {state.points.map((p, i) => (
                <circle key={i} cx={sx(p.x)} cy={sy(p.y)} r={2} fill={depthColour(p.depth)} opacity={0.8} />
              ))}
              {/* Low-spot marker */}
              <circle cx={sx(stats.lowSpot.x)} cy={sy(stats.lowSpot.y)} r={6}
                fill="none" stroke="#f59e0b" strokeWidth={2} />
              <text x={sx(stats.lowSpot.x) + 9} y={sy(stats.lowSpot.y) + 4}
                fontSize={10} fill="#f59e0b">low spot</text>
            </svg>
            <div className="text-[10px] text-slate-500 mt-1">
              Colour scale: light blue = shallow ({fmt(stats.min, 1)} m), dark blue = deep ({fmt(stats.max, 1)} m).
            </div>
          </div>
        </>
      ) : (
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-8 text-center">
          <Upload className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <div className="text-sm text-slate-300">No bathymetric data loaded yet</div>
          <div className="text-xs text-slate-500 mt-1">Upload a CSV to begin</div>
        </div>
      )}

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-rb-brand" /> What happens next
        </div>
        <div className="text-xs text-slate-400 leading-relaxed">
          Parsed points are persisted to <code className="bg-slate-800 px-1 rounded">rb_bathymetry_v1</code>{' '}
          and available to the layout optimiser, the plume-coverage tiler and the sediment bed physics
          module. Once site-measured bathymetry is uploaded, every depth-dependent calculation in the tool
          (SOTE, plume radius, Shields shear) re-runs against the actual contour rather than the assumed
          flat polygon.
        </div>
      </div>
    </div>
  );
}
